import React, { useMemo } from "react";
import {
  Table,
  Tag,
  Tooltip,
  Typography,
  Descriptions,
} from "@pankod/refine-antd";
import dayjs from "dayjs";
import {
  PrizmaScreen,
  PrizmaScreenStatus,
  GAP_KIND_LABELS,
  OPEN_HOURS_LABEL,
  STATUS_COLORS,
  STATUS_LABELS,
  formatDateTime,
  formatFromNow,
  formatDuration,
  formatVideo,
  shopLabel,
  screenLabel,
} from "../prizma";

interface PrizmaScreensTableProps {
  screens: PrizmaScreen[];
  loading: boolean;
}

// Kısa değerler (zaman, süre, sayı) satır kırmasın; tablo yatay kaydırılır.
const nowrap = {
  onCell: () => ({ style: { whiteSpace: "nowrap" as const } }),
};

const byTime = (a?: string | null, b?: string | null) =>
  (a ? dayjs(a).valueOf() : 0) - (b ? dayjs(b).valueOf() : 0);

// Sorunlu ekranlar önce (API'deki sırayla aynı)
const STATUS_ORDER: Record<PrizmaScreenStatus, number> = {
  silent: 0,
  notOpened: 1,
  offHours: 2,
  online: 3,
};

const ExpandedRow: React.FC<{ screen: PrizmaScreen }> = ({ screen }) => (
  <>
    <Descriptions size="small" column={{ xs: 1, md: 2, xl: 3 }} bordered>
      <Descriptions.Item label="Cihaz UUID">
        {screen.deviceUuid}
      </Descriptions.Item>
      <Descriptions.Item label="OMMA cihaz ID">
        {screen.ommaDeviceId ?? "-"}
      </Descriptions.Item>
      <Descriptions.Item label="OMMA mağaza adı">
        {screen.shopName || "-"}
      </Descriptions.Item>
      <Descriptions.Item label="Bölge">
        {screen.shop?.area || "-"}
      </Descriptions.Item>
      <Descriptions.Item label="Satış müdürü">
        {screen.shop?.salesManager || "-"}
      </Descriptions.Item>
      <Descriptions.Item label="Bölge müdürü">
        {screen.shop?.areaManager || "-"}
      </Descriptions.Item>
      <Descriptions.Item label="İlk görülme">
        {formatDateTime(screen.firstSeenAt)}
      </Descriptions.Item>
      <Descriptions.Item label="Oturum başlangıcı">
        {formatDateTime(screen.sessionStartedAt)}
      </Descriptions.Item>
      <Descriptions.Item label="Son dokunma">
        {formatDateTime(screen.lastTouchAt)}
      </Descriptions.Item>
      <Descriptions.Item label="Bugün ilk / son sinyal">
        {formatDateTime(screen.today.firstSeenAt)} /{" "}
        {formatDateTime(screen.today.lastSeenAt)}
      </Descriptions.Item>
      <Descriptions.Item label="Art arda dokunmalı sinyal">
        {screen.touchStreak}
      </Descriptions.Item>
      <Descriptions.Item label="Video">
        {screen.video
          ? `readyState ${screen.video.readyState}, ${screen.video.currentTime} sn`
          : "-"}
      </Descriptions.Item>
    </Descriptions>
    {screen.diagnostics && (
      <>
        <Typography.Text strong style={{ display: "block", marginTop: 12 }}>
          Tanı paketi
        </Typography.Text>
        <pre style={{ maxHeight: 300, overflow: "auto", fontSize: 12 }}>
          {JSON.stringify(screen.diagnostics, null, 2)}
        </pre>
      </>
    )}
  </>
);

export const PrizmaScreensTable: React.FC<PrizmaScreensTableProps> = ({
  screens,
  loading,
}) => {
  const columns = useMemo(
    () => [
      {
        title: "Durum",
        key: "status",
        ...nowrap,
        filters: (Object.keys(STATUS_LABELS) as PrizmaScreenStatus[]).map(
          (status) => ({ text: STATUS_LABELS[status], value: status })
        ),
        onFilter: (value: any, s: PrizmaScreen) => s.status === value,
        sorter: (a: PrizmaScreen, b: PrizmaScreen) =>
          STATUS_ORDER[a.status] - STATUS_ORDER[b.status],
        render: (_: unknown, s: PrizmaScreen) => (
          <Tag color={STATUS_COLORS[s.status]}>{STATUS_LABELS[s.status]}</Tag>
        ),
      },
      {
        title: "Mağaza",
        key: "shop",
        width: 220,
        sorter: (a: PrizmaScreen, b: PrizmaScreen) =>
          String(a.shopCode).localeCompare(String(b.shopCode)),
        render: (_: unknown, s: PrizmaScreen) => (
          <>
            <div>
              {shopLabel(s)}{" "}
              {!s.shopMatched && (
                <Tooltip title="OMMA'daki mağaza adı ekranim mağaza listesiyle eşleşmedi">
                  <Tag color="orange">Eşleşmedi</Tag>
                </Tooltip>
              )}
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {[s.shopCode, s.shop?.city].filter(Boolean).join(" · ")}
            </Typography.Text>
          </>
        ),
      },
      {
        title: "Ekran",
        key: "screen",
        ...nowrap,
        sorter: (a: PrizmaScreen, b: PrizmaScreen) =>
          a.screen.localeCompare(b.screen),
        render: (_: unknown, s: PrizmaScreen) => screenLabel(s),
      },
      {
        title: "Son sinyal",
        key: "lastSeenAt",
        ...nowrap,
        sorter: (a: PrizmaScreen, b: PrizmaScreen) =>
          byTime(a.lastSeenAt, b.lastSeenAt),
        render: (_: unknown, s: PrizmaScreen) => (
          <Tooltip title={formatDateTime(s.lastSeenAt)}>
            {formatFromNow(s.lastSeenAt)}
          </Tooltip>
        ),
      },
      {
        title: "Son dokunma",
        key: "lastTouchAt",
        ...nowrap,
        sorter: (a: PrizmaScreen, b: PrizmaScreen) =>
          byTime(a.lastTouchAt, b.lastTouchAt),
        render: (_: unknown, s: PrizmaScreen) =>
          s.lastTouchAt ? (
            <Tooltip title={formatDateTime(s.lastTouchAt)}>
              {formatFromNow(s.lastTouchAt)}
            </Tooltip>
          ) : (
            <Typography.Text type="secondary">Henüz yok</Typography.Text>
          ),
      },
      {
        title: (
          <Tooltip
            title={`Son dokunmadan beri ekranın açık kaldığı süre. Sadece ${OPEN_HOURS_LABEL} arası sayılır.`}
          >
            Dokunmasız açık
          </Tooltip>
        ),
        key: "openSecSinceLastTouch",
        ...nowrap,
        sorter: (a: PrizmaScreen, b: PrizmaScreen) =>
          a.openSecSinceLastTouch - b.openSecSinceLastTouch,
        render: (_: unknown, s: PrizmaScreen) =>
          formatDuration(s.openSecSinceLastTouch),
      },
      {
        title: "Bugün",
        children: [
          {
            title: (
              <Tooltip
                title={`Bugün ${OPEN_HOURS_LABEL} arasında açık kaldığı süre`}
              >
                Açık
              </Tooltip>
            ),
            key: "todayOpen",
            ...nowrap,
            sorter: (a: PrizmaScreen, b: PrizmaScreen) =>
              a.today.openMinutes - b.today.openMinutes,
            render: (_: unknown, s: PrizmaScreen) =>
              formatDuration(s.today.openMinutes * 60),
          },
          {
            title: "Dokunma",
            key: "todayTouches",
            ...nowrap,
            sorter: (a: PrizmaScreen, b: PrizmaScreen) =>
              a.today.touches - b.today.touches,
            render: (_: unknown, s: PrizmaScreen) => s.today.touches,
          },
          {
            title: "Popup",
            key: "todayPopupOpens",
            ...nowrap,
            sorter: (a: PrizmaScreen, b: PrizmaScreen) =>
              a.today.popupOpens - b.today.popupOpens,
            render: (_: unknown, s: PrizmaScreen) => s.today.popupOpens,
          },
          {
            title: "Yeniden başlama",
            key: "todayRestarts",
            ...nowrap,
            sorter: (a: PrizmaScreen, b: PrizmaScreen) =>
              a.today.restarts - b.today.restarts,
            render: (_: unknown, s: PrizmaScreen) => s.today.restarts,
          },
        ],
      },
      {
        title: "Video",
        key: "video",
        ...nowrap,
        render: (_: unknown, s: PrizmaScreen) => formatVideo(s),
      },
      {
        title: "Son kesinti",
        key: "lastGap",
        ...nowrap,
        render: (_: unknown, s: PrizmaScreen) =>
          s.lastGap ? (
            <Tooltip
              title={`${formatDateTime(s.lastGap.from)} → ${formatDateTime(
                s.lastGap.to
              )}`}
            >
              {GAP_KIND_LABELS[s.lastGap.kind]} ·{" "}
              {formatDuration(
                dayjs(s.lastGap.to).diff(dayjs(s.lastGap.from), "second")
              )}
            </Tooltip>
          ) : (
            "-"
          ),
      },
      {
        title: "Build",
        key: "build",
        ...nowrap,
        render: (_: unknown, s: PrizmaScreen) => s.build || "-",
      },
    ],
    []
  );

  return (
    <Table
      dataSource={screens}
      columns={columns}
      rowKey="deviceUuid"
      loading={loading}
      expandable={{
        expandedRowRender: (s: PrizmaScreen) => <ExpandedRow screen={s} />,
      }}
      pagination={{
        defaultPageSize: 25,
        showSizeChanger: true,
        showTotal: (t) => `Toplam ${t.toLocaleString("tr-TR")} ekran`,
      }}
      size="small"
      scroll={{ x: "max-content" }}
    />
  );
};
